import { UseQueryExtendedOptions } from "@bn/api";
import { useSharedPortfolioContext } from "@providers/SharedPortfolioProvider";

type GeneratedFunction<P, O, R> = (params: P, options?: O) => R;

export const usePublicOrPrivateData = <
  TParams,
  TData,
  TOptions extends UseQueryExtendedOptions<TData, AxiosApiError, TData>,
  TFunc extends GeneratedFunction<TParams, TOptions, ReturnType<TFunc>>,
  TFunc2 extends GeneratedFunction<
    TParams & { publicId: string },
    TOptions,
    ReturnType<TFunc2>
  >,
>({
  privateMethod,
  publicMethod,
  params,
  options = {} as TOptions,
}: {
  privateMethod: TFunc;
  publicMethod: TFunc2;
  params: TParams;
  options?: TOptions;
}): ReturnType<TFunc | TFunc2> => {
  const { isSharedPortfolio, publicId } = useSharedPortfolioContext();
  const isEnabled = options?.enabled === undefined ? true : options?.enabled;

  const privateResult = privateMethod(params, {
    ...options,
    enabled: !isSharedPortfolio && isEnabled,
  });
  const publicResult = publicMethod(
    {
      publicId: publicId!,
      ...params,
    },
    {
      ...options,
      enabled: isSharedPortfolio && Boolean(publicId) && isEnabled,
    }
  );

  return isSharedPortfolio ? publicResult : privateResult;
};
